// Administração → Formulários → editor (pedido 2026-10-05). Perguntas,
// Aparência (cor, fundo, banner, mensagens), Respostas e Compartilhar, com
// pré-visualização ao vivo ao lado. Salva sozinho ~1 s depois de cada
// alteração (sem popup de confirmação — padrão do sistema).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  CloudOff,
  Copy,
  ExternalLink,
  Eye,
  ImagePlus,
  Inbox,
  Loader2,
  MessageCircle,
  Monitor,
  Palette,
  Share2,
  Smartphone,
  Trash2,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { FormBuilder } from "@/components/forms/form-builder";
import { FormRenderer } from "@/components/forms/form-renderer";
import { FormResponses } from "@/components/forms/form-responses";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { useAdminBranding } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import {
  FORMS_QUERY_KEY,
  gpsDriveFormsService,
  publicFormUrl,
  type CompanyForm,
  type FormBackgroundStyle,
  type FormInput,
  type FormTheme,
} from "@/services/gpsdrive-forms.service";

export const Route = createFileRoute("/admin/forms/$id")({
  component: () => (
    <AdminAccessGate action="formularios">
      <FormEditorPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Editar formulário — Administração — Wp Rastreamento" }] }),
});

const COLOR_PRESETS = ["#2563eb", "#7c3aed", "#db2777", "#dc2626", "#ea580c", "#ca8a04", "#16a34a", "#0891b2", "#0f172a"];
const BG_PRESETS = ["#0f172a", "#020617", "#1e1b4b", "#111827", "#f1f5f9", "#ffffff"];

const BACKGROUNDS: { key: FormBackgroundStyle; label: string; hint: string }[] = [
  { key: "aurora", label: "Aurora", hint: "Luzes coloridas se movendo" },
  { key: "gradient", label: "Degradê", hint: "Cores em movimento contínuo" },
  { key: "dots", label: "Pontilhado", hint: "Textura com brilho suave" },
  { key: "solid", label: "Sólido", hint: "Uma cor só, discreto" },
];

function toInput(f: CompanyForm): FormInput {
  return {
    title: f.title,
    description: f.description,
    active: f.active,
    sections: f.sections,
    theme: f.theme,
    successTitle: f.successTitle,
    successMessage: f.successMessage,
  };
}

type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

function FormEditorPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data: loaded, isLoading, error } = useQuery({
    queryKey: ["forms", id],
    queryFn: () => gpsDriveFormsService.get(id),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data: branding } = useAdminBranding();

  const [draft, setDraft] = useState<FormInput | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState("perguntas");
  const [focusStep, setFocusStep] = useState(0);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const lastSaved = useRef<string>("");

  // Carrega uma vez — depois o rascunho local manda (refetch não pode
  // atropelar o que a pessoa está digitando).
  useEffect(() => {
    if (loaded && !draft) {
      const input = toInput(loaded);
      setDraft(input);
      setBannerUrl(loaded.bannerUrl);
      setCustomLogoUrl(loaded.customLogoUrl ?? null);
      lastSaved.current = JSON.stringify(input);
    }
  }, [loaded, draft]);

  // Salvamento automático com debounce.
  useEffect(() => {
    if (!draft) return;
    const snapshot = JSON.stringify(draft);
    if (snapshot === lastSaved.current) return;
    setSaveState("pending");
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        const saved = await gpsDriveFormsService.update(id, draft);
        lastSaved.current = snapshot;
        setSaveState("saved");
        setSaveError(null);
        qc.setQueryData(["forms", id], saved);
        void qc.invalidateQueries({ queryKey: FORMS_QUERY_KEY, exact: true });
      } catch (e) {
        setSaveState("error");
        setSaveError(e instanceof Error ? e.message : "Não foi possível salvar");
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [draft, id, qc]);

  const patch = (p: Partial<FormInput>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchTheme = (p: Partial<FormTheme>) => setDraft((d) => (d ? { ...d, theme: { ...d.theme, ...p } } : d));

  const previewForm = useMemo(
    () =>
      draft && {
        ...draft,
        bannerUrl: bannerUrl ? resolveGpsDriveUrl(bannerUrl) : null,
        logoUrl: customLogoUrl
          ? resolveGpsDriveUrl(customLogoUrl)
          : branding?.logoUrl
            ? resolveGpsDriveUrl(branding.logoUrl)
            : null,
        companyName: branding?.serverName ?? null,
      },
    [draft, bannerUrl, customLogoUrl, branding?.logoUrl, branding?.serverName],
  );

  if (isLoading || (!draft && !error)) {
    return (
      <div className="flex justify-center p-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !draft || !loaded || !previewForm) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-destructive">{(error as Error | null)?.message ?? "Formulário não encontrado"}</p>
        <Button variant="outline" asChild>
          <Link to="/admin/forms">
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
          </Link>
        </Button>
      </div>
    );
  }

  const unread = loaded.unreadCount;

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/forms" title="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <Input
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          className="h-10 max-w-md flex-1 border-transparent bg-transparent text-xl font-bold shadow-none hover:border-input focus-visible:border-input"
        />
        <SaveIndicator state={saveState} error={saveError} />
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={draft.active} onCheckedChange={(v) => patch({ active: v })} />
            <span className={draft.active ? "font-medium text-emerald-600" : "text-muted-foreground"}>
              {draft.active ? "Recebendo respostas" : "Pausado"}
            </span>
          </label>
          <Button variant="outline" size="sm" asChild>
            <a href={publicFormUrl(loaded.slug)} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1.5 h-4 w-4" /> Abrir
            </a>
          </Button>
        </div>
      </div>
      {saveState === "error" && saveError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Não foi salvo: {saveError}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] 2xl:grid-cols-[minmax(0,7fr)_minmax(0,6fr)]">
        {/* Editor */}
        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="perguntas" className="gap-1.5">
              <Wand2 className="h-4 w-4" /> <span className="hidden sm:inline">Perguntas</span>
            </TabsTrigger>
            <TabsTrigger value="aparencia" className="gap-1.5">
              <Palette className="h-4 w-4" /> <span className="hidden sm:inline">Aparência</span>
            </TabsTrigger>
            <TabsTrigger value="respostas" className="relative gap-1.5">
              <Inbox className="h-4 w-4" /> <span className="hidden sm:inline">Respostas</span>
              {unread > 0 && (
                <span className="ml-1 rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">{unread}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="compartilhar" className="gap-1.5">
              <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">Compartilhar</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="perguntas" className="mt-4 space-y-4">
            <div className="space-y-2 rounded-2xl border p-4">
              <Label className="text-xs text-muted-foreground">Descrição (aparece abaixo do título)</Label>
              <Textarea
                value={draft.description}
                rows={3}
                placeholder="Ex.: Preencha seus dados para concluir o cadastro."
                onChange={(e) => patch({ description: e.target.value })}
              />
            </div>
            <FormBuilder
              sections={draft.sections}
              onChange={(sections) => patch({ sections })}
              onFocusSection={setFocusStep}
            />
          </TabsContent>

          <TabsContent value="aparencia" className="mt-4 space-y-4">
            <AppearanceEditor
              form={loaded}
              draft={draft}
              bannerUrl={bannerUrl}
              onBanner={(f) => setBannerUrl(f.bannerUrl)}
              customLogoUrl={customLogoUrl}
              companyLogoUrl={branding?.logoUrl ? resolveGpsDriveUrl(branding.logoUrl) : null}
              onLogo={(f) => setCustomLogoUrl(f.customLogoUrl ?? null)}
              patch={patch}
              patchTheme={patchTheme}
            />
          </TabsContent>

          <TabsContent value="respostas" className="mt-4">
            <FormResponses form={loaded} />
          </TabsContent>

          <TabsContent value="compartilhar" className="mt-4">
            <SharePanel form={loaded} title={draft.title} active={draft.active} />
          </TabsContent>
        </Tabs>

        {/* Prévia */}
        <div className="hidden lg:block">
          <div className="sticky top-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Eye className="h-3.5 w-3.5" /> Pré-visualização ao vivo
              </span>
              <div className="flex rounded-lg border p-0.5">
                <button
                  type="button"
                  className={cn("rounded-md p-1.5", device === "desktop" && "bg-muted")}
                  onClick={() => setDevice("desktop")}
                  title="Computador"
                >
                  <Monitor className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className={cn("rounded-md p-1.5", device === "mobile" && "bg-muted")}
                  onClick={() => setDevice("mobile")}
                  title="Celular"
                >
                  <Smartphone className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div
              className={cn(
                "mx-auto overflow-hidden border shadow-xl transition-all duration-500",
                device === "mobile" ? "w-[380px] rounded-[2.2rem] border-[10px] border-slate-900" : "w-full rounded-2xl",
              )}
            >
              <div data-gdf-scroll className="h-[calc(100vh-150px)] overflow-y-auto">
                <FormRenderer form={previewForm} preview focusStep={tab === "perguntas" ? focusStep : undefined} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveIndicator({ state, error }: { state: SaveState; error: string | null }) {
  if (state === "idle") return null;
  if (state === "error")
    return (
      <span className="flex items-center gap-1 text-xs text-destructive" title={error ?? ""}>
        <CloudOff className="h-3.5 w-3.5" /> Não salvo
      </span>
    );
  if (state === "saved")
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-600">
        <Check className="h-3.5 w-3.5" /> Salvo
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Salvando…
    </span>
  );
}

function Section({ title, children, hint }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-2xl border p-4">
      <div>
        <p className="font-semibold">{title}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function ColorRow({
  value,
  presets,
  onChange,
}: {
  value: string;
  presets: string[];
  onChange: (c: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={cn(
            "h-8 w-8 rounded-full border-2 transition-transform hover:scale-110",
            value.toLowerCase() === c ? "border-foreground ring-2 ring-foreground/20" : "border-white shadow",
          )}
          style={{ background: c }}
          title={c}
        />
      ))}
      <label className="relative flex h-8 items-center gap-2 rounded-full border px-2 text-xs">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-5 w-5 cursor-pointer rounded-full border-0 bg-transparent p-0"
        />
        {value.toUpperCase()}
      </label>
    </div>
  );
}

function AppearanceEditor({
  form,
  draft,
  bannerUrl,
  onBanner,
  customLogoUrl,
  companyLogoUrl,
  onLogo,
  patch,
  patchTheme,
}: {
  form: CompanyForm;
  draft: FormInput;
  bannerUrl: string | null;
  onBanner: (f: CompanyForm) => void;
  customLogoUrl: string | null;
  companyLogoUrl: string | null;
  onLogo: (f: CompanyForm) => void;
  patch: (p: Partial<FormInput>) => void;
  patchTheme: (p: Partial<FormTheme>) => void;
}) {
  const t = draft.theme;
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  // Logotipo próprio do formulário (pedido 2026-10-09).
  const logoFileRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const uploadLogo = async (file: File) => {
    setLogoUploading(true);
    setLogoError(null);
    try {
      onLogo(await gpsDriveFormsService.uploadLogo(form.id, file));
      patchTheme({ showLogo: true });
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : "Não foi possível enviar o logotipo");
    } finally {
      setLogoUploading(false);
    }
  };
  const shownLogo = customLogoUrl ? resolveGpsDriveUrl(customLogoUrl) : companyLogoUrl;

  const upload = async (file: File) => {
    setUploading(true);
    setBannerError(null);
    try {
      onBanner(await gpsDriveFormsService.uploadBanner(form.id, file));
    } catch (e) {
      setBannerError(e instanceof Error ? e.message : "Não foi possível enviar a imagem");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Section title="Cor principal" hint="Botões, barra de progresso, destaques e opções marcadas.">
        <ColorRow value={t.primaryColor} presets={COLOR_PRESETS} onChange={(c) => patchTheme({ primaryColor: c })} />
      </Section>

      <Section title="Fundo da página">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {BACKGROUNDS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => patchTheme({ backgroundStyle: b.key })}
              className={cn(
                "overflow-hidden rounded-xl border-2 text-left transition-all hover:-translate-y-0.5",
                t.backgroundStyle === b.key ? "border-primary shadow-md" : "border-transparent",
              )}
            >
              <div className="h-16" style={bgSwatch(b.key, t.primaryColor, t.backgroundColor)} />
              <div className="bg-card px-2 py-1.5">
                <p className="text-xs font-semibold">{b.label}</p>
                <p className="text-[10px] leading-tight text-muted-foreground">{b.hint}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Cor de fundo</Label>
          <ColorRow value={t.backgroundColor} presets={BG_PRESETS} onChange={(c) => patchTheme({ backgroundColor: c })} />
        </div>
      </Section>

      <Section title="Banner" hint="Imagem no topo do formulário — aparece inteira, no formato da imagem (ex.: 1600 × 500).">
        {bannerUrl ? (
          <div className="relative overflow-hidden rounded-xl border">
            <img src={resolveGpsDriveUrl(bannerUrl)} alt="" className="block h-auto w-full" />
            <div className="absolute right-2 top-2 flex gap-1.5">
              <Button size="sm" variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
                Trocar
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={uploading}
                onClick={async () => {
                  setUploading(true);
                  try {
                    onBanner(await gpsDriveFormsService.removeBanner(form.id));
                  } finally {
                    setUploading(false);
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          >
            {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
            {uploading ? "Enviando…" : "Enviar imagem do banner"}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void upload(f);
          }}
        />
        {bannerError && <p className="text-xs text-destructive">{bannerError}</p>}
      </Section>

      <Section title="Logotipo" hint="Aparece no topo do formulário. Sem logotipo próprio, usa o da empresa (Identidade Visual).">
        <label className="flex items-center justify-between gap-2 rounded-xl border p-3 text-sm">
          Mostrar logotipo
          <Switch checked={t.showLogo} onCheckedChange={(v) => patchTheme({ showLogo: v })} />
        </label>
        <div className="flex items-center gap-4 rounded-xl border p-3">
          <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted/40 p-2">
            {shownLogo ? (
              <img src={shownLogo} alt="" className="max-h-full max-w-full object-contain" />
            ) : (
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-xs text-muted-foreground">
              {customLogoUrl ? "Logotipo próprio deste formulário" : "Usando o logotipo da empresa"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="secondary" disabled={logoUploading} onClick={() => logoFileRef.current?.click()}>
                {logoUploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1.5 h-3.5 w-3.5" />}
                {customLogoUrl ? "Trocar logotipo" : "Enviar logotipo"}
              </Button>
              {customLogoUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={logoUploading}
                  onClick={async () => {
                    setLogoUploading(true);
                    try {
                      onLogo(await gpsDriveFormsService.removeLogo(form.id));
                    } finally {
                      setLogoUploading(false);
                    }
                  }}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Usar o da empresa
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Tamanho</Label>
            <span className="text-xs text-muted-foreground">{t.logoSize ?? 64}px</span>
          </div>
          <Slider
            min={32}
            max={200}
            step={4}
            value={[t.logoSize ?? 64]}
            onValueChange={([v]) => patchTheme({ logoSize: v })}
          />
        </div>
        <input
          ref={logoFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void uploadLogo(f);
          }}
        />
        {logoError && <p className="text-xs text-destructive">{logoError}</p>}
      </Section>

      <Section title="Estilo">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-2 rounded-xl border p-3 text-sm">
            Cartão escuro
            <Switch checked={t.dark} onCheckedChange={(v) => patchTheme({ dark: v })} />
          </label>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Cantos</Label>
          <div className="flex gap-2">
            {(
              [
                ["md", "Retos"],
                ["lg", "Médios"],
                ["xl", "Arredondados"],
              ] as const
            ).map(([k, label]) => (
              <Button
                key={k}
                size="sm"
                variant={t.rounded === k ? "default" : "outline"}
                onClick={() => patchTheme({ rounded: k })}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Texto do botão de envio</Label>
          <Input value={t.submitText} maxLength={40} onChange={(e) => patchTheme({ submitText: e.target.value })} />
        </div>
      </Section>

      <Section title="Tela de agradecimento" hint="Aparece depois do envio, com animação de confete.">
        <Input value={draft.successTitle} onChange={(e) => patch({ successTitle: e.target.value })} placeholder="Título" />
        <Textarea
          value={draft.successMessage}
          rows={3}
          onChange={(e) => patch({ successMessage: e.target.value })}
          placeholder="Mensagem"
        />
      </Section>
    </>
  );
}

function bgSwatch(style: FormBackgroundStyle, p: string, bg: string): React.CSSProperties {
  switch (style) {
    case "solid":
      return { background: bg };
    case "gradient":
      return { background: `linear-gradient(120deg, ${bg}, ${p}, color-mix(in srgb, ${p} 35%, #a855f7))` };
    case "dots":
      return {
        backgroundColor: bg,
        backgroundImage: `radial-gradient(color-mix(in srgb, ${p} 70%, transparent) 1px, transparent 1px)`,
        backgroundSize: "8px 8px",
      };
    default:
      return {
        background: `radial-gradient(circle at 20% 30%, ${p}, transparent 55%), radial-gradient(circle at 80% 40%, color-mix(in srgb, ${p} 45%, #a855f7), transparent 50%), radial-gradient(circle at 50% 100%, color-mix(in srgb, ${p} 40%, #06b6d4), transparent 55%), ${bg}`,
      };
  }
}

function SharePanel({ form, title, active }: { form: CompanyForm; title: string; active: boolean }) {
  const url = publicFormUrl(form.slug);
  const [copied, setCopied] = useState(false);
  const message = `${title}\nPreencha pelo link: ${url}`;
  return (
    <div className="space-y-4">
      {!active && (
        <div className="rounded-lg border border-amber-400/50 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 dark:text-amber-300">
          O formulário está pausado — quem abrir o link verá "Formulário indisponível".
        </div>
      )}
      <Section title="Link do formulário" hint="Qualquer pessoa com o link pode responder, sem precisar de login.">
        <div className="flex gap-2">
          <Input readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-sm" />
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              } catch {
                // sem clipboard — o campo já seleciona o texto ao focar
              }
            }}
          >
            {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer">
              <MessageCircle className="mr-1.5 h-4 w-4 text-emerald-600" /> Enviar pelo WhatsApp
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={url} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1.5 h-4 w-4" /> Abrir formulário
            </a>
          </Button>
        </div>
      </Section>
    </div>
  );
}
